import { useState } from "react";
import { Clock, CheckCircle2, ArrowRight, BadgePercent, Briefcase, Sparkles, X } from "lucide-react";
import { WA_LINK } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const FEE_CATS = new Set(["ai-computer"]);

const fmt = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

export const CourseCard = ({ course, categoryName }) => {
  const [open, setOpen] = useState(false);
  const showFees = FEE_CATS.has(course.category);

  return (
    <>
      <article
        data-testid={`course-card-${course.id}`}
        className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0A192F]/5 transition-all duration-300 flex flex-col"
      >
        <div className="relative h-44 overflow-hidden">
          <img
            src={course.image}
            alt={course.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/60 via-transparent to-transparent" />
          <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] uppercase tracking-[0.14em] font-bold text-[#0A192F]">
            {categoryName}
          </span>
          <span className="absolute bottom-3 left-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00B37E] text-white text-xs font-semibold">
            <Clock className="w-3.5 h-3.5" /> {course.duration}
          </span>
        </div>

        <div className="p-5 flex-1 flex flex-col">
          <h3 className="font-heading text-lg font-bold text-[#0A192F] leading-snug">
            {course.name}
          </h3>

          {/* Skills preview (AI-Computer) or outcomes (others) */}
          {(course.skills?.length ? course.skills : course.outcomes || []).slice(0, 4).length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {(course.skills?.length ? course.skills : course.outcomes || []).slice(0, 4).map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#334155]">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#00B37E] flex-shrink-0" />
                  <span className="line-clamp-1">{o}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Careers (AI-Computer only) */}
          {showFees && course.careers?.length ? (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-[#64748B]">
              <Briefcase className="w-3.5 h-3.5 text-[#00B37E]" />
              <span className="truncate"><b className="text-[#0A192F]">Careers:</b> {course.careers.slice(0, 2).join(" · ")}</span>
            </div>
          ) : null}

          {/* Fees (AI-Computer only) */}
          {showFees && (course.actual_fee > 0 || course.monthly_fee > 0) ? (
            <div className="mt-4 rounded-xl bg-[#F8FAF9] border border-slate-100 p-3">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <div><span className="text-[#64748B]">Registration:</span> <b className="text-[#0A192F]">{fmt(course.registration_fee)}</b></div>
                <div><span className="text-[#64748B]">Monthly:</span> <b className="text-[#0A192F]">{fmt(course.monthly_fee)}</b></div>
              </div>
              {course.actual_fee > 0 ? (
                <div className="mt-1.5 flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Total</span>
                  <span className="font-heading text-lg font-extrabold text-[#00B37E]">{fmt(course.actual_fee)}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex-1" />

          <div className="mt-5 flex items-center gap-2">
            <Button
              onClick={() => setOpen(true)}
              className="flex-1 bg-[#0A192F] hover:bg-[#0F2648] text-white rounded-full h-10 text-sm font-semibold"
              data-testid={`course-view-${course.id}`}
            >
              View Program
            </Button>
            <a
              href={WA_LINK(`Hi TASSEI, I want details about "${course.name}"`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`course-enquire-${course.id}`}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#00B37E]/12 text-[#00B37E] hover:bg-[#00B37E] hover:text-white transition-colors"
              aria-label="Enquire on WhatsApp"
            >
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </article>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid={`course-detail-${course.id}`}>
          <div className="relative aspect-[16/8] overflow-hidden bg-slate-100">
            <img src={course.image} alt={course.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/85 via-[#0A192F]/40 to-transparent" />
            <div className="absolute bottom-4 left-5 right-5 text-white">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-[0.18em] font-bold">
                {categoryName}
              </span>
              <DialogHeader className="mt-2 text-left">
                <DialogTitle className="font-heading text-2xl md:text-3xl font-extrabold text-white leading-tight">
                  {course.name}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#7CF0BD]">
                <Clock className="w-3.5 h-3.5" /> {course.duration}
              </div>
            </div>
          </div>

          <div className="px-6 py-6 space-y-5">
            {course.skills?.length ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#00B37E] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Key Skills / Curriculum</div>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {course.skills.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#334155]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#00B37E] flex-shrink-0" /><span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : course.outcomes?.length ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#00B37E]">Overview</div>
                <ul className="mt-3 space-y-2">
                  {course.outcomes.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#334155]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#00B37E] flex-shrink-0" /><span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {showFees && course.careers?.length ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#00B37E] flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Career Opportunities</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {course.careers.map((c, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-[#0A192F] text-white text-xs font-semibold">{c}</span>
                  ))}
                </div>
              </div>
            ) : null}

            {showFees && (course.actual_fee > 0 || course.monthly_fee > 0) ? (
              <div className="rounded-2xl bg-[#F8FAF9] border border-slate-200 p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#00B37E]">Fee Structure</div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                  <div><div className="text-[10px] uppercase text-[#64748B] font-bold tracking-wider">Registration</div><div className="mt-1 font-heading text-lg font-extrabold text-[#0A192F]">{fmt(course.registration_fee)}</div></div>
                  <div><div className="text-[10px] uppercase text-[#64748B] font-bold tracking-wider">Monthly</div><div className="mt-1 font-heading text-lg font-extrabold text-[#0A192F]">{fmt(course.monthly_fee)}</div></div>
                  <div><div className="text-[10px] uppercase text-[#64748B] font-bold tracking-wider">Total</div><div className="mt-1 font-heading text-lg font-extrabold text-[#00B37E]">{fmt(course.actual_fee)}</div></div>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={WA_LINK(`Hi TASSEI, I want to apply for "${course.name}"`)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
                data-testid={`course-apply-${course.id}`}
              >
                <Button className="w-full bg-[#00B37E] hover:bg-[#009166] text-white rounded-full h-11 font-semibold">
                  Apply / Enquire Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full h-11">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourseCard;
